(() => {
  const CONFIG = window.SETUP_FEE_CONFIG || {};
  const log = (...args) => CONFIG.debug && console.log("[setup-fee]", ...args);

  // Hard single-flight: everyone awaits the same in-flight run
  let inFlightPromise = null;

  // Prevent reload loops on /cart
  const CART_RELOAD_FLAG = "setup_fee_cart_reloaded_once";

  async function fetchJSON(url, opts = {}) {
    const res = await fetch(url, { credentials: "same-origin", cache: "no-store", ...opts });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Fetch failed ${res.status} ${url}: ${text}`);
    }
    return await res.json();
  }

  function isOurInternalCall(init) {
    try {
      const h = init?.headers;
      if (!h) return false;
      if (h instanceof Headers) return h.get("X-Setup-Fee") === "1";
      return String(h["X-Setup-Fee"] || h["x-setup-fee"] || "") === "1";
    } catch {
      return false;
    }
  }

  async function getCart() {
    return await fetchJSON("/cart.js");
  }

  async function addVariantToCart(variantId) {
    return await fetchJSON("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Setup-Fee": "1" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
  }

  async function changeLineItemQuantity(lineKey, quantity) {
    return await fetchJSON("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Setup-Fee": "1" },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
  }

  function feeLines(cart) {
    return (cart.items || []).filter(i => i.variant_id === CONFIG.setupFeeVariantId);
  }

  function cartHasTrigger(cart) {
    const needles = Array.isArray(CONFIG.triggerTitleContains)
      ? CONFIG.triggerTitleContains.map(s => String(s).toLowerCase()).filter(Boolean)
      : [];

    if (!needles.length) return false;

    return (cart.items || []).some(item => {
      // never let the fee line count as a trigger
      if (item.variant_id === CONFIG.setupFeeVariantId) return false;

      const title = String(item.product_title || item.title || "").toLowerCase();
      return needles.some(n => title.includes(n));
    });
  }

  function reloadCartOnce() {
    if (window.location.pathname !== "/cart") return;

    try {
      if (sessionStorage.getItem(CART_RELOAD_FLAG) === "1") return;
      sessionStorage.setItem(CART_RELOAD_FLAG, "1");
    } catch (_) {
      // if storage blocked, still reload once (worst case user might reload twice)
    }

    log("Reloading /cart once to sync Ecomus UI");
    window.location.reload();
  }

  function clearReloadGuard() {
    if (window.location.pathname !== "/cart") return;
    try { sessionStorage.removeItem(CART_RELOAD_FLAG); } catch (_) {}
  }

  async function ensureFeeCorrect(reason) {
    if (!CONFIG.setupFeeVariantId) {
      console.warn("[setup-fee] Missing setupFeeVariantId");
      return;
    }

    const removeWhenNotNeeded = CONFIG.removeFeeWhenNotNeeded !== false;

    // 1) Load cart
    let cart = await getCart();

    // 2) HARD SAFETY: dedupe fee lines FIRST (prevents overcharge)
    let lines = feeLines(cart);
    if (lines.length > 1) {
      log("DEDUPE fee lines:", lines.length, "reason:", reason);
      for (let i = 1; i < lines.length; i++) {
        await changeLineItemQuantity(lines[i].key, 0);
      }
      cart = await getCart();
      lines = feeLines(cart);
    }

    // 3) Determine desired state
    const needsFee = cartHasTrigger(cart);
    let feeLine = lines[0] || null;

    log("ensure", { reason, needsFee, feePresent: !!feeLine, feeQty: feeLine?.quantity });

    let mutated = false;

    if (needsFee) {
      if (!feeLine) {
        // Double-check immediately before add (prevents two runners both adding)
        const cart2 = await getCart();
        const lines2 = feeLines(cart2);
        if (lines2.length === 0) {
          await addVariantToCart(CONFIG.setupFeeVariantId);
          mutated = true;
        } else {
          feeLine = lines2[0];
        }
      } else if (feeLine.quantity !== 1) {
        await changeLineItemQuantity(feeLine.key, 1);
        mutated = true;
      }
    } else if (feeLine && removeWhenNotNeeded) {
      await changeLineItemQuantity(feeLine.key, 0);
      mutated = true;
    }

    // 4) If on /cart and we mutated, force ONE reload so Ecomus re-renders correctly
    if (window.location.pathname === "/cart") {
      if (mutated) {
        reloadCartOnce();
      } else {
        // If cart is stable, allow future reloads when a mutation happens
        clearReloadGuard();
      }
    }
  }

  function run(reason) {
    if (!inFlightPromise) {
      inFlightPromise = (async () => {
        try {
          await ensureFeeCorrect(reason);
        } catch (e) {
          console.warn("[setup-fee] run error:", e);
        } finally {
          inFlightPromise = null;
        }
      })();
    }
    return inFlightPromise;
  }

  function isCartMutationUrl(url) {
    return (
      url.includes("/cart/add") ||
      url.includes("/cart/add.js") ||
      url.includes("/cart/change") ||
      url.includes("/cart/change.js") ||
      url.includes("/cart/update") ||
      url.includes("/cart/update.js")
    );
  }

  // Hook fetch (DO NOT modify responses)
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async (input, init) => {
      const url = typeof input === "string" ? input : input?.url || "";
      const method = init?.method?.toUpperCase() || "GET";

      const res = await originalFetch(input, init);

      try {
        if (method === "POST" && res.ok && isCartMutationUrl(url) && !isOurInternalCall(init)) {
          setTimeout(() => run(`fetch:${url}`), 0);
        }
      } catch (_) {}

      return res;
    };
  }

  // XHR hook (best effort)
  const XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;

    XHR.prototype.open = function(method, url, ...rest) {
      this.__sf_url = url || "";
      this.__sf_method = (method || "GET").toUpperCase();
      return originalOpen.call(this, method, url, ...rest);
    };

    XHR.prototype.send = function(body) {
      this.addEventListener("load", () => {
        try {
          const url = this.__sf_url || "";
          const method = this.__sf_method || "GET";
          const ok = this.status >= 200 && this.status < 300;

          if (ok && method === "POST" && isCartMutationUrl(url)) {
            setTimeout(() => run(`xhr:${url}`), 0);
          }
        } catch (_) {}
      });

      return originalSend.call(this, body);
    };
  }

  // Run on cart load too (covers direct /cart visits)
  document.addEventListener("DOMContentLoaded", () => run("load"));
  window.addEventListener("pageshow", () => run("pageshow"));

  window.ensureSetupFeeCorrect = () => run("manual");
})();
